import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import AlertBanner from './components/AlertBanner';
import MonitoringCard from './components/MonitoringCard';
import RiskScoreChart from './components/RiskScoreChart';
import SuspiciousTransactionFeed from './components/SuspiciousTransactionFeed';
import FraudPatternAnalysis from './components/FraudPatternAnalysis';
import MLModelIndicator from './components/MLModelIndicator';
import { fraudAlertsService, transactionsService } from '../../services/supabaseService';

const FraudDetectionCenter = () => {
  const [riskLevelFilter, setRiskLevelFilter] = useState('all');
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loading, setLoading] = useState(true);
  const [fraudAlerts, setFraudAlerts] = useState([]);
  const [suspiciousTransactions, setSuspiciousTransactions] = useState([]);
  const [monitoringMetrics, setMonitoringMetrics] = useState([]);

  const riskLevelOptions = [
  { value: 'all', label: 'Semua Level Risiko' },
  { value: 'critical', label: 'Kritis (≥80%)' },
  { value: 'high', label: 'Tinggi (60-79%)' },
  { value: 'medium', label: 'Sedang (40-59%)' },
  { value: 'low', label: 'Rendah (<40%)' }];


  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch fraud alerts
        const { data: alerts } = await fraudAlertsService?.getAll({
          isResolved: false,
          limit: 50
        });

        // Fetch fraud statistics
        const { data: stats } = await fraudAlertsService?.getStatistics();

        // Fetch high-risk transactions
        const { data: transactions } = await transactionsService?.getAll({ limit: 50 });
        const highRiskTransactions = transactions?.filter((t) => t?.riskScore > 60) || [];

        if (alerts) {
          setFraudAlerts(alerts);
        }

        if (highRiskTransactions) {
          setSuspiciousTransactions(highRiskTransactions?.map((t) => ({
            id: t?.id,
            merchantName: t?.merchants?.merchantName || 'Unknown Merchant',
            merchantLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_1b3eccd42-1770170853209.png",
            merchantLogoAlt: 'Merchant logo',
            transactionId: t?.transactionId,
            amount: `Rp ${parseFloat(t?.amount || 0)?.toLocaleString('id-ID')}`,
            paymentMethod: t?.paymentMethod,
            riskScore: t?.riskScore || 0,
            status: t?.status === 'success' ? 'investigating' : t?.status === 'pending' ? 'pending' : 'blocked',
            timestamp: new Date(t?.timestamp)?.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
            riskIndicators: [
            t?.riskScore > 80 ? 'IP Mencurigakan' : null,
            t?.riskScore > 70 ? 'Pola Tidak Biasa' : null,
            parseFloat(t?.amount) > 5000000 ? 'Nilai Tinggi' : null]?.
            filter(Boolean)
          })));
        }

        if (stats) {
          const totalTransactions = transactions?.length || 1;
          const fraudCount = alerts?.length || 0;
          const fraudRate = fraudCount / totalTransactions * 100;
          const blockedAmount = alerts?.reduce((sum, a) => sum + a?.transactionCount * 8500000, 0) || 0;

          setMonitoringMetrics([
          {
            title: 'Tingkat Deteksi Fraud',
            value: `${fraudRate?.toFixed(1)}%`,
            subtitle: 'dari total transaksi',
            icon: 'Shield',
            trend: 'down',
            trendValue: '-0.8%',
            threshold: 'Target: <5%',
            status: fraudRate < 5 ? 'success' : 'warning'
          },
          {
            title: 'False Positive Rate',
            value: '12.5%',
            subtitle: 'transaksi valid terblokir',
            icon: 'AlertTriangle',
            trend: 'up',
            trendValue: '+2.1%',
            threshold: 'Target: <10%',
            status: 'warning'
          },
          {
            title: 'Nilai Transaksi Diblokir',
            value: `Rp ${(blockedAmount / 1000000)?.toFixed(1)} Jt`,
            subtitle: 'hari ini',
            icon: 'Ban',
            trend: 'up',
            trendValue: '+15.3%',
            threshold: null,
            status: 'critical'
          },
          {
            title: 'Rata-rata Skor Risiko',
            value: stats?.avgRiskScore?.toFixed(1) || '0',
            subtitle: 'dari 100',
            icon: 'Activity',
            trend: 'down',
            trendValue: '-5.2',
            threshold: 'Ambang: 75',
            status: 'success'
          }]
          );
        }
      } catch (error) {
        console.error('Error fetching fraud data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [riskLevelFilter]);

  // Real-time subscription for fraud alerts
  useEffect(() => {
    const unsubscribe = fraudAlertsService?.subscribe((payload) => {
      if (payload?.eventType === 'INSERT' && payload?.new) {
        setFraudAlerts((prev) => [payload?.new, ...prev]);
      } else if (payload?.eventType === 'UPDATE' && payload?.new) {
        setFraudAlerts((prev) => prev?.map((a) => a?.id === payload?.new?.id ? payload?.new : a));
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        console.log('Auto-refreshing fraud detection data...');
      }, 30000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const criticalAlerts = fraudAlerts?.filter((a) => a?.severity === 'critical') || [];
  const highAlerts = fraudAlerts?.filter((a) => a?.severity === 'high') || [];

  const riskScoreData = [
  { time: '00:00', riskScore: 45, volume: 234 },
  { time: '02:00', riskScore: 52, volume: 189 },
  { time: '04:00', riskScore: 38, volume: 156 },
  { time: '06:00', riskScore: 61, volume: 298 },
  { time: '08:00', riskScore: 73, volume: 445 },
  { time: '10:00', riskScore: 68, volume: 523 },
  { time: '12:00', riskScore: 82, volume: 612 },
  { time: '14:00', riskScore: 76, volume: 587 },
  { time: '16:00', riskScore: 89, volume: 634 },
  { time: '18:00', riskScore: 71, volume: 498 },
  { time: '20:00', riskScore: 64, volume: 423 },
  { time: '22:00', riskScore: 58, volume: 367 },
  { time: '24:00', riskScore: 49, volume: 289 }];


  const geographicData = [
  { id: 'geo-001', city: 'Jakarta Selatan', province: 'DKI Jakarta', fraudCount: criticalAlerts?.length || 0, riskLevel: 92 },
  { id: 'geo-002', city: 'Surabaya', province: 'Jawa Timur', fraudCount: highAlerts?.length || 0, riskLevel: 78 },
  { id: 'geo-003', city: 'Bandung', province: 'Jawa Barat', fraudCount: Math.floor(fraudAlerts?.length * 0.3), riskLevel: 71 },
  { id: 'geo-004', city: 'Medan', province: 'Sumatera Utara', fraudCount: Math.floor(fraudAlerts?.length * 0.2), riskLevel: 64 },
  { id: 'geo-005', city: 'Semarang', province: 'Jawa Tengah', fraudCount: Math.floor(fraudAlerts?.length * 0.15), riskLevel: 58 }];


  const paymentMethodData = [
  { id: 'pm-001', name: 'Kartu Kredit', icon: 'CreditCard', transactionCount: '2.847', fraudRate: 8.4, vulnerability: 'critical', commonPatterns: ['Card Testing', 'Stolen Cards', 'CNP Fraud'] },
  { id: 'pm-002', name: 'E-Wallet (OVO/DANA/GoPay)', icon: 'Smartphone', transactionCount: '5.234', fraudRate: 4.2, vulnerability: 'medium', commonPatterns: ['Account Takeover', 'Phishing'] },
  { id: 'pm-003', name: 'Virtual Account', icon: 'Building', transactionCount: '3.156', fraudRate: 2.1, vulnerability: 'low', commonPatterns: ['Payment Mismatch', 'Duplicate Transfer'] },
  { id: 'pm-004', name: 'QRIS', icon: 'QrCode', transactionCount: '4.892', fraudRate: 3.6, vulnerability: 'medium', commonPatterns: ['QR Code Swap', 'Merchant Impersonation'] }];


  const mlModelData = {
    status: 'active',
    accuracy: 94.7,
    responseTime: 85,
    trainingDataSize: '2.4M',
    confidenceScore: 92,
    activeFeatures: ['Transaction Velocity', 'Device Fingerprinting', 'Behavioral Analysis', 'Geographic Anomaly', 'Payment Pattern', 'Merchant Risk Score'],
    lastUpdated: '2026-02-03 23:45:00',
    version: 'v3.2.1'
  };

  const handleAcknowledgeAlert = (alertId) => {
    console.log('Alert acknowledged:', alertId);
  };

  const handleInvestigateAlert = (alert) => {
    console.log('Investigating alert:', alert);
  };

  const handleInvestigateTransaction = (transaction) => {
    console.log('Investigating transaction:', transaction);
  };

  const handleExportReport = () => {
    console.log('Exporting fraud detection report...');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="main-content">
        <div className="content-container">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                Pusat Deteksi Fraud
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Monitoring real-time dan analisis pola transaksi mencurigakan
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Select
                options={riskLevelOptions}
                value={riskLevelFilter}
                onChange={setRiskLevelFilter}
                placeholder="Filter Level Risiko"
                className="w-full sm:w-64" />

              <Button
                variant="outline"
                iconName={audioEnabled ? 'Volume2' : 'VolumeX'}
                iconPosition="left"
                onClick={() => setAudioEnabled(!audioEnabled)}>

                {audioEnabled ? 'Audio On' : 'Audio Off'}
              </Button>
              <Button
                variant="default"
                iconName="Download"
                iconPosition="left"
                onClick={handleExportReport}>

                Export Laporan
              </Button>
            </div>
          </div>

          <AlertBanner
            alerts={[...criticalAlerts, ...highAlerts]}
            onAcknowledge={handleAcknowledgeAlert}
            onInvestigate={handleInvestigateAlert} />


          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            {monitoringMetrics?.map((metric, index) =>
            <MonitoringCard key={index} {...metric} />
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
            <div className="lg:col-span-2">
              <RiskScoreChart data={riskScoreData} />
            </div>
            <div className="lg:col-span-1">
              <SuspiciousTransactionFeed
                transactions={suspiciousTransactions}
                onInvestigate={handleInvestigateTransaction} />

            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
            <div className="lg:col-span-2">
              <FraudPatternAnalysis
                geographicData={geographicData}
                paymentMethodData={paymentMethodData} />

            </div>
            <div className="lg:col-span-1">
              <MLModelIndicator modelData={mlModelData} />
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-4 md:p-6 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="RefreshCw" size={24} className={`text-primary ${autoRefresh ? 'animate-spin' : ''}`} />
                </div>
                <div>
                  <h3 className="text-sm md:text-base font-semibold text-foreground">
                    Auto-Refresh {autoRefresh ? 'Aktif' : 'Nonaktif'}
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    {autoRefresh ? 'Data diperbarui setiap 30 detik' : 'Refresh manual diperlukan'}
                  </p>
                </div>
              </div>
              <Button
                variant={autoRefresh ? 'destructive' : 'default'}
                iconName={autoRefresh ? 'Pause' : 'Play'}
                iconPosition="left"
                onClick={() => setAutoRefresh(!autoRefresh)}>

                {autoRefresh ? 'Hentikan' : 'Aktifkan'} Auto-Refresh
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>);

};

export default FraudDetectionCenter;